import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";

// Copy supplied verbatim by the owner (2026-09-28). Preserve certified vs
// licensed, the three license levels (Single-Site / Multi-Site /
// Enterprise-System), the 12-month organizational cycle, and the IP
// restrictions. No CCCHAPS, no pricing, discounts, package quantities,
// contract terms, enrollment dates, CE credits, accreditation, testimonials,
// statistics. No checkout, payment, application, renewal, or licensing
// documents built yet.

const ctaCls =
  "inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 whitespace-nowrap";
const ctaGoldCls =
  "inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover transition-colors duration-200 whitespace-nowrap";

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Organizational Licensing for the From Diagnosis to Goodbye Program
        </p>
        <h1 className="mt-5 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
          Bring From Diagnosis to Goodbye Into Your Organization
        </h1>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            From Diagnosis to Goodbye can provide organizations with a
            structured way to create meaningful conversations around serious
            illness, caregiving, end of life, and grief while keeping the
            voices of the people living the journey at the center.
          </p>
          <p>
            Organizational licensing is designed for organizations that want
            to formally implement the From Diagnosis to Goodbye program
            within their services, education, support programs, ministries,
            or communities.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div
          className="mt-16 bg-navy-deep rounded-md px-8 sm:px-14 py-14 sm:py-20 text-center"
          data-testid="organizations-statement"
        >
          <div className="max-w-3xl mx-auto font-serif text-2xl sm:text-4xl leading-snug text-ivory">
            <p>
              A facilitator is <em className="text-gold">certified</em>.
            </p>
            <p className="mt-3">
              An organization is <em className="text-gold">licensed</em>.
            </p>
          </div>
          <p className="mt-8 text-ivory/70 max-w-2xl mx-auto leading-relaxed">
            Individual certification and organizational licensing work
            together—but they are not the same thing.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function Why() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Why Organizational Licensing?
          </p>
          <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
            <p>
              A Certified From Diagnosis to Goodbye Facilitator receives
              individual authorization to facilitate the approved program
              according to current standards.
            </p>
            <p>
              That individual certification does not automatically authorize
              an employer or organization to reproduce, distribute, modify,
              institutionalize, or implement the program throughout its
              locations, departments, workforce, or service system.
            </p>
            <p>
              Organizations seeking formal or recurring implementation may
              require an organizational license appropriate to the scope of
              use.
            </p>
          </div>
          <p className="mt-10 text-stone2">
            Organizational licensing helps establish clear expectations for:
          </p>
          <div className="mt-4">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {[
                "How the program may be implemented",
                "Who may facilitate it",
                "Which materials may be used",
                "How the program may be represented publicly",
                "How intellectual property is protected",
                "How program updates are communicated",
                "How implementation remains consistent with the purpose of the work",
              ].map((t) => (
                <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <blockquote
            className="mt-12 max-w-3xl border-l-2 border-gold pl-5 sm:pl-6 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
            data-testid="organizations-principle"
          >
            Certification prepares the person. Licensing authorizes the
            organization.
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

const WHO = [
  "Hospices",
  "Palliative-care programs",
  "Hospitals and healthcare organizations",
  "Senior-living communities",
  "Assisted-living communities",
  "Churches and ministry organizations",
  "Bereavement programs",
  "Caregiver-support programs",
  "Community organizations",
  "Educational organizations",
  "Professional-development programs",
  "Other approved organizations seeking formal implementation",
];

function WhoFor() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Who Organizational Licensing Is For
        </p>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight">
          Organizational licensing may be appropriate for:
        </h2>
        <div className="mt-8 flex flex-wrap gap-3">
          {WHO.map((w) => (
            <span
              key={w}
              className="bg-sand border border-line rounded-full px-5 py-2.5 text-sm text-navy"
            >
              {w}
            </span>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-stone2 leading-relaxed">
          Licensing requirements may vary depending on how broadly an
          organization intends to use the program.
        </p>
      </Reveal>
    </section>
  );
}

const LEVELS = [
  {
    t: "Single-Site License",
    b1: "Designed for an organization implementing the program at one approved location, ministry, department, or program site.",
    b2: "This may be appropriate for a local hospice office, church, senior-living community, bereavement program, caregiver-support program, or similar single-site setting.",
    b3: "The license defines the approved scope of organizational use.",
  },
  {
    t: "Multi-Site License",
    b1: "Designed for an organization implementing the program across multiple approved locations or program sites under the same organization.",
    b2: "This may be appropriate for organizations operating several facilities, hospice offices, churches, community locations, or related programs.",
    b3: "The license defines which locations or programs are included within the authorized scope.",
  },
  {
    t: "Enterprise / System License",
    b1: "Designed for larger organizations or systems seeking broader implementation across multiple facilities, departments, regions, or programs.",
    b2: "This may be appropriate for healthcare systems, larger hospice organizations, senior-living networks, denominational or ministry systems, and other organizations requiring broader implementation.",
    b3: "Enterprise or system licensing would be structured according to the organization's intended scope of use.",
  },
];

function Levels() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Three Levels of Organizational Licensing
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
            Built to match the scope of use.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {LEVELS.map((l, i) => (
            <Reveal key={l.t} delay={0.06 * i} className="h-full">
              <div
                className="h-full bg-ivory border border-line rounded-md p-8 hover:border-gold/60 hover:shadow-[0_20px_40px_-28px_rgba(11,21,34,0.3)] transition-all duration-300"
                data-testid={`organizations-level-${i + 1}`}
              >
                <span className="font-serif text-gold text-lg">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-serif text-xl sm:text-2xl text-navy leading-snug">
                  {l.t}
                </h3>
                <p className="mt-4 text-stone2 leading-relaxed text-[15px]">{l.b1}</p>
                <p className="mt-3 text-stone2 leading-relaxed text-[15px]">{l.b2}</p>
                <p className="mt-4 border-t border-line pt-4 font-serif italic text-navy text-[15px]">
                  {l.b3}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Essential() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Certified Facilitators Remain Essential
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            An organizational license does not replace facilitator
            certification.
          </p>
          <p>
            Individuals delivering the protected From Diagnosis to Goodbye
            program must meet the facilitator requirements applicable to the
            program and maintain active certification where required.
          </p>
        </div>
        <blockquote
          className="mt-12 max-w-4xl border-l-2 border-gold pl-6 sm:pl-8 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
          data-testid="organizations-essential-statement"
        >
          &ldquo;The organization carries the license.
          <br />
          The individual carries the certification.&rdquo;
        </blockquote>
        <div className="mt-8 max-w-3xl space-y-5 text-stone2 leading-relaxed">
          <p>
            If a Certified Facilitator leaves an organization, the
            individual's certification remains with the individual.
          </p>
          <p>
            Likewise, an organization's license does not become the personal
            property of an employee or facilitator.
          </p>
          <p>
            This separation helps protect both the credential and the
            organizational implementation.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function MayInclude() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            What Organizational Licensing May Include
          </p>
          <p className="mt-6 max-w-3xl text-lg text-stone2 leading-relaxed">
            Depending on the license level and implementation model,
            authorized organizational participation may include access to
            items such as:
          </p>
          <div className="mt-6 max-w-4xl">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {[
                "Program implementation guidance",
                "Authorized facilitator resources",
                "Approved program descriptions",
                "Approved promotional language",
                "Authorized branding guidance",
                "Participant-material guidance",
                "Program updates",
                "Administrative implementation support",
                "Facilitator certification pathways for appropriate team members",
                "Future evaluation or outcomes resources as developed",
              ].map((t) => (
                <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-8 max-w-3xl text-stone2 leading-relaxed">
            Specific resources and terms may vary by license level and
            current program offerings.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Materials() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Participant Materials
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            Organizational licensing does not automatically provide
            unlimited reproduction rights to books, workbooks, participant
            guides, or other protected materials.
          </p>
          <p>
            Organizations may be required to purchase or otherwise obtain
            authorized participant materials according to the applicable
            program terms.
          </p>
          <p>
            This protects both the integrity of the participant experience
            and the intellectual property supporting the program.
          </p>
        </div>
        <blockquote
          className="mt-12 max-w-4xl border-l-2 border-gold pl-5 sm:pl-6 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
          data-testid="organizations-materials-principle"
        >
          A license provides authorized use. It does not transfer ownership
          of the materials.
        </blockquote>
      </Reveal>
    </section>
  );
}

const NOT_ALLOW = [
  "Claim ownership of the From Diagnosis to Goodbye program",
  "Rewrite or materially alter the protected curriculum",
  "Rebrand the program as its own",
  "Remove required From Diagnosis to Goodbye attribution",
  "Create derivative versions of the program",
  "Create its own From Diagnosis to Goodbye facilitator certification",
  "Train or certify facilitators outside the authorized certification process",
  "Sell or sublicense the program to other organizations",
  "Share licensed resources outside the approved scope",
  "Upload the protected curriculum to an unrestricted LMS or digital platform",
  "Reproduce books, workbooks, guides, or participant materials beyond authorized permissions",
  "Continue representing itself as an authorized program site after the applicable license expires or is terminated",
];

function NotAllow() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            What an Organizational License Does Not Allow
          </p>
          <p className="mt-6 max-w-3xl text-lg text-stone2 leading-relaxed">
            Unless separately authorized in writing, an organizational
            license does not permit an organization to:
          </p>
          <div className="mt-6 max-w-4xl">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {NOT_ALLOW.map((t) => (
                <li
                  key={t}
                  className="flex gap-3 leading-relaxed text-stone2"
                >
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <blockquote
            className="mt-12 max-w-4xl border-l-2 border-gold pl-5 sm:pl-6 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
            data-testid="organizations-adapt-statement"
          >
            &ldquo;Adapt the conversation to the people. Do not appropriate
            or rewrite the program.&rdquo;
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

function Branding() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Organizational Branding
        </p>
        <p className="mt-8 max-w-3xl text-lg text-stone2 leading-relaxed">
          Organizations with an active applicable license may be permitted
          to describe themselves using approved language such as:
        </p>
        <div className="mt-6 max-w-3xl space-y-3">
          <p className="bg-sand border border-line rounded-md px-6 py-4 font-serif text-xl text-navy">
            Authorized From Diagnosis to Goodbye Program Site
          </p>
          <p className="text-center font-serif text-gold">or</p>
          <p className="bg-sand border border-line rounded-md px-6 py-4 font-serif text-xl text-navy">
            Licensed to Offer the From Diagnosis to Goodbye Program
          </p>
        </div>
        <div className="mt-8 max-w-3xl space-y-5 text-stone2 leading-relaxed">
          <p>
            An organization should not describe itself as a Certified From
            Diagnosis to Goodbye Organization unless a future credential
            specifically authorizes that terminology.
          </p>
          <p>
            The word <span className="font-medium text-navy">Certified</span>{" "}
            is reserved for qualifying individual facilitators under the
            current program structure.
          </p>
          <p>
            The word <span className="font-medium text-navy">Licensed</span>{" "}
            describes authorized organizational implementation.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

const RESPONSIBILITIES = [
  "Maintain the required number of appropriately certified facilitators",
  "Use current authorized program materials",
  "Follow applicable program standards",
  "Respect intellectual property and branding requirements",
  "Protect participant privacy and dignity",
  "Maintain appropriate safety and referral procedures",
  "Avoid misrepresenting the program or organizational authorization",
  "Ensure facilitators operate within appropriate professional and organizational boundaries",
  "Stop or modify program use if organizational authorization expires, changes, or is withdrawn",
  "Address reported misuse or program-integrity concerns appropriately",
];

function Responsibilities() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Organizational Responsibilities
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight">
            Organizations participating in the program are expected to:
          </h2>
          <div className="mt-8 max-w-4xl">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {RESPONSIBILITIES.map((t) => (
                <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-8 max-w-3xl text-stone2 leading-relaxed">
            Detailed responsibilities will be established in the applicable
            Organizational Program License Agreement.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Annual() {
  return (
    <section className="bg-navy-deep" data-testid="organizations-annual-band">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Annual Organizational Licensing
          </p>
          <h2 className="mt-5 max-w-3xl font-serif text-3xl sm:text-4xl text-ivory tracking-tight leading-[1.15]">
            Organizational licensing is intended to operate on an annual
            authorization cycle.
          </h2>
          <div className="mt-10 inline-block border border-gold/40 rounded-md px-10 py-8 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-ivory/70">
              A typical organizational license period will be
            </p>
            <p className="mt-3 font-serif text-4xl sm:text-5xl text-gold">
              12 Months
            </p>
          </div>
          <p className="mt-8 max-w-3xl text-ivory/70 leading-relaxed">
            Annual licensing allows the program to maintain current
            implementation standards, facilitator requirements, resources,
            branding, and program updates.
          </p>
          <p className="mt-4 max-w-3xl text-ivory/70 leading-relaxed">
            Organizational renewal is separate from individual facilitator
            renewal.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <blockquote
            className="mt-12 max-w-4xl border-l-2 border-gold pl-6 sm:pl-8 font-serif italic text-2xl sm:text-3xl text-ivory leading-snug"
            data-testid="organizations-renewal-statement"
          >
            &ldquo;Facilitators renew their certification. Organizations
            renew their license.&rdquo;
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

function Choosing() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Choosing the Appropriate License
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            The appropriate licensing level depends on how an organization
            intends to implement the program.
          </p>
          <p>
            A single local program and a healthcare system operating across
            multiple facilities do not require the same implementation
            structure.
          </p>
          <p>
            Before organizational licensing is issued, the intended use may
            be reviewed to determine:
          </p>
        </div>
        <div className="mt-6 max-w-4xl">
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            {[
              "Number of locations",
              "Number of programs or departments",
              "Expected number of facilitators",
              "Expected participant reach",
              "Intended use of participant materials",
              "Digital or in-person implementation",
              "Internal versus public-facing programming",
              "Any special implementation requirements",
            ].map((t) => (
              <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                <span className="shrink-0 font-serif text-gold">—</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-8 max-w-3xl text-stone2 leading-relaxed">
          This allows the authorization to reflect the organization's actual
          use rather than forcing every organization into the same model.
        </p>
      </Reveal>
    </section>
  );
}

function Closing() {
  return (
    <section className="bg-sand border-t border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Interested in Bringing the Program to Your Organization?
          </p>
          <div className="mt-8 max-w-3xl mx-auto space-y-5 text-lg text-stone2 leading-relaxed">
            <p>
              Formal organizational licensing is being developed as part of
              the From Diagnosis to Goodbye Facilitator Program.
            </p>
            <p>
              Organizations interested in future implementation may begin by
              learning about the facilitator methodology and certification
              requirements.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/facilitators/training"
              data-testid="organizations-cta-training"
              className={ctaGoldCls}
            >
              Explore Facilitator Training <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/facilitators/certification"
              data-testid="organizations-cta-certification"
              className={ctaCls}
            >
              Review Certification Requirements <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <Link
            to="/facilitators"
            data-testid="organizations-back"
            className="mt-10 inline-flex items-center gap-2 text-sm text-stone2 hover:text-gold transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Facilitator Program
            Overview
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Protecting() {
  return (
    <section className="bg-navy-deep">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Protecting the Voice While Expanding the Reach
          </p>
          <div className="mt-8 max-w-3xl mx-auto space-y-5 text-lg text-ivory/70 leading-relaxed">
            <p>
              The purpose of organizational licensing is not simply to
              control where the program appears.
            </p>
            <p>
              It is to help ensure that as From Diagnosis to Goodbye reaches
              more people, the principles at the heart of the work remain
              intact.
            </p>
          </div>
          <div className="mt-10 max-w-3xl mx-auto space-y-2 font-serif text-xl sm:text-2xl text-ivory leading-snug">
            <p>The patient remains more than the diagnosis.</p>
            <p>The family remains more than the crisis.</p>
            <p>The caregiver remains more than the tasks they perform.</p>
            <p>
              The helper remembers that expertise should inform the room
              without owning the room.
            </p>
            <p>
              And grief is given space to speak without being forced into
              someone else's timeline or interpretation.
            </p>
          </div>
          <div
            className="mt-14 font-serif text-3xl sm:text-5xl text-gold leading-tight"
            data-testid="organizations-closing-statement"
          >
            <p>See the person.</p>
            <p>Hear the voice.</p>
            <p>Honor the journey.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function FacilitatorOrganizations() {
  return (
    <div className="bg-ivory" data-testid="facilitators-organizations-page">
      <Hero />
      <Why />
      <WhoFor />
      <Levels />
      <Essential />
      <MayInclude />
      <Materials />
      <NotAllow />
      <Branding />
      <Responsibilities />
      <Annual />
      <Choosing />
      <Closing />
      <Protecting />
    </div>
  );
}
